import classNames from 'classnames';
import { AnchorLink } from 'components/anchorLink';
import { Icon, toIconsEnum } from 'components/Icons';
import { selectIsActiveIppica } from 'features/configuration/selectors';
import { Counter } from 'features/live/liveNav/components/counter/Counter';
import { useDisciplina } from 'features/live/liveNav/hooks';
import { selectIsLiveNavLoading, selectTs } from 'features/live/liveNav/selectors';
import { miIppica } from 'features/live/liveNav/utility';
import { useInternalSession } from 'hooks/useInternalSession';
import { useTranslation } from 'hooks/useLingUI';
import { useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useRef } from 'react';
import { isServerSide } from 'utility/constant';
import { gtag, toSportUrlCase } from 'utility/functions';
import styles from './SportTopNavigationItem.module.scss';

export type SportTopNavigationItemProps = {
  isLive?: Boolean | boolean;
  isFixed?: Boolean | boolean;
  idDisciplina: string;
};

export const SportTopNavigationItem = memo(
  function SportTopNavigationItem({ isLive, isFixed, idDisciplina }: SportTopNavigationItemProps) {
    const { t } = useTranslation();
    const { basePath } = useRouter();

    const ts = useTypedSelector(selectTs);
    const isLoading = useTypedSelector(selectIsLiveNavLoading);
    const isActiveIppica = useTypedSelector(selectIsActiveIppica);

    const { session } = useInternalSession();
    const { user } = session ?? {};
    const { token, cardNumber } = user ?? {};

    const { slug, descrizione, descrizioneTrKey, isRoot, isSelected, isActive, customDescription, counter } =
      useDisciplina(idDisciplina, isLive);
    const selectedPrev = useRef(false);
    const description = t(customDescription, descrizione);

    const { hidden, disciplinaIcon } = useMemo(() => {
      const counterOk = isLive ? counter > 0 : true;
      const visible = isActive && (isRoot || counterOk);

      let iconId: string | undefined;
      if (idDisciplina === '-1') {
        iconId = '001';
      } else if (['00', '01'].includes(idDisciplina)) {
        iconId = undefined;
      } else {
        iconId = idDisciplina;
      }

      return {
        hidden: !visible,
        disciplinaIcon: iconId ? toIconsEnum(`DISCIPLINA_${iconId}`) : null,
      };
    }, [isLive, counter, isActive, isRoot, idDisciplina]);

    const getColorByVertical = (isPrimary: Boolean | boolean | undefined): string =>
      `var(--color-${isPrimary ? 'primary' : 'accent'})`;

    const href = useMemo(() => {
      if (idDisciplina === miIppica.id && !isActiveIppica) {
        return token && cardNumber
          ? `${process.env.NEXT_PUBLIC_SEAMLESS_BASE_URL}/seamless-login/accesso/${token}/${cardNumber}/newsito/1/`
          : `${process.env.NEXT_PUBLIC_SEAMLESS_BASE_URL}/ippica`;
      }
      return toSportUrlCase(`/${slug}`);
    }, [cardNumber, idDisciplina, isActiveIppica, slug, token]);

    useEffect(() => {
      if (isServerSide) return;

      if (selectedPrev.current !== isSelected) {
        selectedPrev.current = isSelected;
        if (isSelected) {
          const element = document.querySelector<HTMLLIElement>(`#sport-menu-item-${idDisciplina}`);
          if (element?.parentElement) {
            const { x, width } = element.getBoundingClientRect();
            const parentWidth = element.parentElement.getBoundingClientRect().width;
            element.parentElement.scrollLeft = x - (parentWidth - width) * 0.5;
          }
        }
      }
    }, [isSelected, idDisciplina]);

    if (hidden) return null;

    return (
      <li
        id={`sport-menu-item-${idDisciplina}`}
        data-testid={`disciplina-${idDisciplina}`}
        data-selected={isSelected}
        suppressHydrationWarning
        className={classNames(styles.container, isLive ? styles.containerLive : styles.containerPrematch, {
          [styles.disabled]: isLoading,
          [styles.fixedItem]: isFixed,
          [styles.activeItem]: isSelected,
        })}
      >
        <Counter key={`${idDisciplina}-${isLive}-${ts}`} isLive={isLive} idDisciplina={idDisciplina} />
        <AnchorLink
          href={href}
          target="_self"
          className={styles.link}
          onClick={() => gtag({ section: 'Sport', detail: `${slug}`, event: 'Click Sport Navigation' }, true)}
        >
          {disciplinaIcon && (
            <Icon
              iconId={disciplinaIcon}
              className={styles.icon}
              color={
                isFixed
                  ? getColorByVertical(isLive)
                  : basePath === slug
                  ? getColorByVertical(!isLive)
                  : 'var(--color-on-bg-secondary)'
              }
            />
          )}
          <span className={styles.description} suppressHydrationWarning={true}>
            {t(descrizioneTrKey, description)}
          </span>
        </AnchorLink>
      </li>
    );
  },
  (prevProps, nextProps) =>
    prevProps.isLive === nextProps.isLive &&
    prevProps.isFixed === nextProps.isFixed &&
    prevProps.idDisciplina === nextProps.idDisciplina
);
